function requiredIso(value,label){
  const text=String(value||'').trim();
  if(!text||Number.isNaN(Date.parse(text)))throw new TypeError(label+' must be an ISO timestamp');
  return text;
}

function optionalIso(value,label){
  if(value==null||value==='')return null;
  return requiredIso(value,label);
}

function localParts(value){
  const text=String(value||'').trim();
  if(!text)return null;
  const match=/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(text);
  if(!match)throw new TypeError('Local datetime must use YYYY-MM-DDTHH:mm');
  const parts={
    year:Number(match[1]),
    month:Number(match[2]),
    day:Number(match[3]),
    hour:Number(match[4]),
    minute:Number(match[5]),
    second:Number(match[6]||0)
  };
  const probe=new Date(Date.UTC(parts.year,parts.month-1,parts.day,parts.hour,parts.minute,parts.second));
  if(
    probe.getUTCFullYear()!==parts.year
    ||probe.getUTCMonth()!==parts.month-1
    ||probe.getUTCDate()!==parts.day
    ||probe.getUTCHours()!==parts.hour
    ||probe.getUTCMinutes()!==parts.minute
    ||probe.getUTCSeconds()!==parts.second
  )throw new TypeError('Local datetime is invalid');
  return parts;
}

function utcOffset(value){
  const number=Number(value);
  if(!Number.isInteger(number)||number<-840||number>840){
    throw new TypeError('utcOffsetMinutes must be an integer between -840 and 840');
  }
  return number;
}

function zonedPartsAt(timestamp,timeZone){
  const formatter=new Intl.DateTimeFormat('en-CA',{
    timeZone,
    year:'numeric',
    month:'2-digit',
    day:'2-digit',
    hour:'2-digit',
    minute:'2-digit',
    second:'2-digit',
    hourCycle:'h23'
  });
  const parts=Object.fromEntries(
    formatter.formatToParts(new Date(timestamp))
      .filter(part=>part.type!=='literal')
      .map(part=>[part.type,Number(part.value)])
  );
  return parts;
}

function localPartsAsUtcMillis(parts){
  return Date.UTC(parts.year,parts.month-1,parts.day,parts.hour,parts.minute,parts.second);
}

function sameLocalParts(left,right){
  return left.year===right.year
    &&left.month===right.month
    &&left.day===right.day
    &&left.hour===right.hour
    &&left.minute===right.minute
    &&left.second===right.second;
}

function zonedLocalDateTimeToIso(parts,timeZone){
  const zone=String(timeZone||'').trim();
  if(!zone)throw new TypeError('timeZone is required');
  // Start from the wall-clock value interpreted as UTC, then iteratively
  // compensate for the zone's offset at the target instant. This makes the
  // conversion use the target date's DST rules instead of today's offset.
  const desired=localPartsAsUtcMillis(parts);
  let guess=desired;
  try{
    for(let iteration=0;iteration<4;iteration+=1){
      const observed=zonedPartsAt(guess,zone);
      const delta=desired-localPartsAsUtcMillis(observed);
      if(delta===0)break;
      guess+=delta;
    }
    const finalParts=zonedPartsAt(guess,zone);
    if(!sameLocalParts(finalParts,parts)){
      throw new TypeError('Local datetime does not exist in the selected timezone');
    }
  }catch(error){
    if(error instanceof TypeError&&/does not exist/.test(error.message))throw error;
    throw new TypeError('Invalid IANA timezone');
  }
  return new Date(guess).toISOString();
}

function offsetMillisAt(timestamp,timeZone){
  const observed=zonedPartsAt(timestamp,timeZone);
  return localPartsAsUtcMillis(observed)-timestamp;
}

function zonedLocalCandidates(parts,timeZone){
  const zone=String(timeZone||'').trim();
  if(!zone)throw new TypeError('timeZone is required');
  const desired=localPartsAsUtcMillis(parts);
  const day=24*60*60*1000;
  const offsets=new Set();
  try{
    for(const delta of [-2*day,-day,0,day,2*day]){
      offsets.add(offsetMillisAt(desired+delta,zone));
    }
  }catch{
    throw new TypeError('Invalid IANA timezone');
  }

  const candidates=[];
  for(const offset of offsets){
    const candidate=desired-offset;
    try{
      if(sameLocalParts(zonedPartsAt(candidate,zone),parts))candidates.push(candidate);
    }catch{
      throw new TypeError('Invalid IANA timezone');
    }
  }
  return [...new Set(candidates)].sort((a,b)=>a-b);
}

export function localDateTimeCandidates(value,{timeZone}={}){
  const parts=localParts(value);
  if(!parts)return Object.freeze([]);
  const candidates=zonedLocalCandidates(parts,timeZone);
  return Object.freeze(candidates.map(millis=>new Date(millis).toISOString()));
}

export function strictLocalDateTimeToIso(value,{timeZone}={}){
  const parts=localParts(value);
  if(!parts)return null;
  const candidates=zonedLocalCandidates(parts,timeZone);
  if(candidates.length===0){
    throw new TypeError('Local datetime does not exist in the selected timezone');
  }
  if(candidates.length>1){
    throw new TypeError('Local datetime is ambiguous in the selected timezone');
  }
  return new Date(candidates[0]).toISOString();
}

export function localDateTimeToIso(value,{utcOffsetMinutes=null,timeZone=null}={}){
  const parts=localParts(value);
  if(!parts)return null;

  if(timeZone)return zonedLocalDateTimeToIso(parts,timeZone);

  if(utcOffsetMinutes==null){
    const local=new Date(
      parts.year,parts.month-1,parts.day,
      parts.hour,parts.minute,parts.second,0
    );
    if(Number.isNaN(local.getTime()))throw new TypeError('Local datetime is invalid');
    return local.toISOString();
  }

  const offset=utcOffset(utcOffsetMinutes);
  const utcMillis=Date.UTC(
    parts.year,parts.month-1,parts.day,
    parts.hour,parts.minute,parts.second,0
  )-offset*60_000;
  return new Date(utcMillis).toISOString();
}

function formatParts(iso,{locale='en-CA',timeZone}={}){
  const value=new Date(requiredIso(iso,'iso'));
  const formatter=new Intl.DateTimeFormat(locale,{
    timeZone,
    year:'numeric',
    month:'2-digit',
    day:'2-digit',
    hour:'2-digit',
    minute:'2-digit',
    hourCycle:'h23'
  });
  const parts=Object.fromEntries(
    formatter.formatToParts(value)
      .filter(part=>part.type!=='literal')
      .map(part=>[part.type,part.value])
  );
  return parts;
}

export function isoToLocalInputValue(iso,{timeZone}={}){
  if(iso==null||iso==='')return '';
  const p=formatParts(iso,{locale:'en-CA',timeZone});
  return p.year+'-'+p.month+'-'+p.day+'T'+p.hour+':'+p.minute;
}

export function formatPlannedDateTime(iso,{
  locale='pl-PL',
  timeZone,
  dateStyle='medium',
  timeStyle='short'
}={}){
  if(iso==null||iso==='')return '';
  const value=new Date(requiredIso(iso,'iso'));
  return new Intl.DateTimeFormat(locale,{timeZone,dateStyle,timeStyle}).format(value);
}
